// BookedSellingScreen.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { Container, Typography } from "@mui/material";

import HotelSelectorMany from "../components/HotelSelectorMany";
import FiltersReport, {
    DEFAULT_FILTERS_ORDER,
} from "../components/FiltersReport";
import {
    COMPARISON_CUSTOM,
    COMPARISON_FILTER,
    COMPARISON_PREVIOUS_MONTH,
    COMPARISON_PREVIOUS_WEEK,
    COMPARISON_PREVIOUS_YEAR,
    COMPARISON_SAME_TIME_LAST_YEAR,
    DATATYPE_CHECKIN,
    DATATYPE_CHECKIN_NAME,
    DATATYPE_RESDATE,
    DATATYPE_RESDATE_NAME,
    DEFAULT_COMPARISON,
    DISPLAY_ELEMENT_HELPER,
    GROUP_COUNTRIES,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
    GROUP_SITE_ID,
    PRESET_ANYTIME,
    PRESET_MONTH_TO_DATE,
    formatDate,
    mergeBookingsWithoutDuplicates,
    processAggregated_,
} from "../helpers/filters";
import ReportBuilder, {
    GRID_HORIZONTAL,
} from "../components/shared/ReportBuilder/ReportBuilder";
import { SanitizeHotelID, URLAPI } from "../configuration";
import ReservationsAggregatedCache from "../helpers/ReservationsAggregatedCache";
import { adjustLeapYear } from "../helpers/dates";
import { set } from "date-fns";
import { TARGET_OFF } from "../helpers/targets";
import { REPORT_HOTEL_SUMMARY_TABLE } from "../components/reports/ReportHotelSummaryTable";
import { REPORT_METRIC_LINE_TREND } from "../components/shared/ReportBuilder/ReportMetricLineTrend";
import { REPORT_METRIC_PIE } from "../components/shared/ReportBuilder/ReportMetricPie";
import { REPORT_METRIC_VS_METRIC_BARS } from "../components/shared/ReportBuilder/ReportMetricVSMetricBars";
import { REPORT_TABLE_METRICS_SUMMARY } from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import { REPORT_TOP_DATES_TABLE } from "../components/shared/ReportBuilder/ReportTopDatesTable";
import DataLoader from "../components/DataLoader";

dayjs.extend(utc);

const AGGREGATED_GROUPS_ALLOWED = [GROUP_COUNTRIES];
const AGGREGATED_GROUPS_BY_HOTEL = [GROUP_SITE_ID, GROUP_COUNTRIES];

const BookedSellingScreen = () => {
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const js = useSelector((state) => state.globalReportFilters);
    const [hotels, setHotels] = useState([
        hotelID === "default" ? "default" : Number(hotelID),
    ]);

    useEffect(() => {
        if (!hotelID) return;

        const nextHotelID = hotelID === "default" ? "default" : Number(hotelID);

        setHotels((prevHotels) =>
            prevHotels.length === 1 &&
            String(prevHotels[0]) === String(nextHotelID)
                ? prevHotels
                : [nextHotelID],
        );
    }, [hotelID]);

    const [dataBookingsByCountry, setDataBookingsByCountry] = useState({});
    const [dataBookingsByCheckIn, setDataBookingsByCheckIn] = useState({});
    const [dataBookingsByResDate, setDataBookingsByResDate] = useState({});
    const [dataBookingsByHotel, setDataBookingsByHotel] = useState({});
    const [dataSearchDatesSummary, setDataSearchDatesSummary] = useState({});
    const [dataSearchedStayDatesSummary, setDataSearchedStayDatesSummary] =
        useState({});

    const bookingsCache = useRef(new Map());
    const CACHE_USES = 8;

    const { user } = useSelector((state) => state.auth);
    const authToken = user?.jwt ?? "";
    const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
    };

    const hotelsKey = useMemo(() => hotels.join(","), [hotels]);

    const [comparisonLabel, setComparisonLabel] = useState("");
    useEffect(() => {
        if (!hotels.length) {
            const emptyData = {
                hotelID: "none",
                status: "loaded",
                data: [],
            };

            setDataBookingsByCountry(emptyData);
            setDataBookingsByCheckIn(emptyData);
            setDataBookingsByResDate(emptyData);
            setDataBookingsByHotel(emptyData);
            setDataSearchDatesSummary(emptyData);
            setDataSearchedStayDatesSummary(emptyData);
            return;
        }
        if (!hotels.length || !js.fromDay || !js.toDay || !js.dataType) return;

        let ignore = false;

        const hotel_ids = hotels.map((h) => SanitizeHotelID(h));

        const comparison = js.filters?.Comparison ?? DEFAULT_COMPARISON;
        setComparisonLabel(comparison);
        const comparisonBase = comparison || DEFAULT_COMPARISON;

        let comparisonFromDay = dayjs(js.fromDay);
        let comparisonToDay = dayjs(js.toDay);

        if (
            comparisonBase === COMPARISON_PREVIOUS_YEAR ||
            comparisonBase === COMPARISON_SAME_TIME_LAST_YEAR
        ) {
            comparisonFromDay = adjustLeapYear(comparisonFromDay).subtract(
                1,
                "year",
            );
            comparisonToDay = adjustLeapYear(comparisonToDay).subtract(
                1,
                "year",
            );
        } else if (comparisonBase === COMPARISON_PREVIOUS_MONTH) {
            comparisonFromDay = comparisonFromDay.subtract(1, "month");
            comparisonToDay = comparisonToDay.subtract(1, "month");
        } else if (comparisonBase === COMPARISON_PREVIOUS_WEEK) {
            comparisonFromDay = comparisonFromDay.subtract(7, "day");
            comparisonToDay = comparisonToDay.subtract(7, "day");
        } else if (
            comparisonBase === COMPARISON_CUSTOM &&
            js.filters?.ComparisonCustom?.fromDay &&
            js.filters?.ComparisonCustom?.toDay
        ) {
            comparisonFromDay = dayjs(js.filters.ComparisonCustom.fromDay);
            comparisonToDay = dayjs(js.filters.ComparisonCustom.toDay);
        }

        comparisonFromDay = formatDate(comparisonFromDay);
        comparisonToDay = formatDate(comparisonToDay);

        const isSameCustomComparisonRange =
            comparisonBase === COMPARISON_CUSTOM &&
            comparisonFromDay === formatDate(js.fromDay) &&
            comparisonToDay === formatDate(js.toDay);

        const comparisonBookingTo =
            comparisonBase === COMPARISON_SAME_TIME_LAST_YEAR &&
            js.dataType !== DATATYPE_RESDATE
                ? formatDate(
                      adjustLeapYear(dayjs.utc().startOf("day")).subtract(
                          1,
                          "year",
                      ),
                  )
                : null;

        const loadAggregatedData = async ({
            groupBy,
            setState,
            withComparison,
            dateBy = js.dataType,
        }) => {
            const comparisonBookingToForDateBy =
                comparisonBase === COMPARISON_SAME_TIME_LAST_YEAR &&
                dateBy !== DATATYPE_RESDATE
                    ? formatDate(
                          adjustLeapYear(dayjs.utc().startOf("day")).subtract(
                              1,
                              "year",
                          ),
                      )
                    : null;

            const cacheKey = ReservationsAggregatedCache.getKey({
                hotelIDs: hotel_ids,
                dateBy,
                groupBy,
                groupByDay: true,
            });

            let cache = bookingsCache.current.get(cacheKey);

            if (!cache || cache.usesLeft <= 0) {
                cache = new ReservationsAggregatedCache({
                    headers,
                    hotelIDs: hotel_ids,
                    dateBy,
                    groupBy,
                    groupByDay: true,
                    maxUses: CACHE_USES,
                });

                bookingsCache.current.set(cacheKey, cache);
            }

            setState({
                status: "loading",
                data: [],
            });

            const currentDataBookings = await cache.get(js.fromDay, js.toDay);

            if (ignore) return;

            setState({
                ...currentDataBookings,
                status: withComparison ? "partiallyLoaded" : "loaded",
            });

            if (!withComparison) return;

            try {
                let comparisonCache = cache;

                if (comparisonBookingToForDateBy) {
                    const comparisonCacheKey =
                        ReservationsAggregatedCache.getKey({
                            hotelIDs: hotel_ids,
                            dateBy,
                            groupBy,
                            groupByDay: true,
                            toBooking: comparisonBookingToForDateBy,
                        });

                    comparisonCache =
                        bookingsCache.current.get(comparisonCacheKey);

                    if (!comparisonCache || comparisonCache.usesLeft <= 0) {
                        comparisonCache = new ReservationsAggregatedCache({
                            headers,
                            hotelIDs: hotel_ids,
                            dateBy,
                            groupBy,
                            groupByDay: true,
                            maxUses: CACHE_USES,
                            toBooking: comparisonBookingToForDateBy,
                        });

                        bookingsCache.current.set(
                            comparisonCacheKey,
                            comparisonCache,
                        );
                    }
                }

                const comparisonDataBookings = await comparisonCache.get(
                    comparisonFromDay,
                    comparisonToDay,
                );

                if (ignore) return;

                const data = [
                    ...mergeBookingsWithoutDuplicates(
                        currentDataBookings.data ?? [],
                    ).map((row) => ({
                        ...row,
                        __comparisonPeriod: "current",
                    })),
                    ...mergeBookingsWithoutDuplicates(
                        comparisonDataBookings.data ?? [],
                    ).map((row) => ({
                        ...row,
                        __comparisonPeriod: "comparison",
                        __skipBookingFilter:
                            comparisonBookingToForDateBy !== null,
                    })),
                ];

                setState({
                    ...currentDataBookings,
                    status: "loaded",
                    data,
                    updatedAt: Date.now(),
                    comparisonCached: comparisonDataBookings.cached,
                    comparisonMissingRanges:
                        comparisonDataBookings.missingRanges,
                });
            } catch (error) {
                if (ignore) return;

                setState({
                    ...currentDataBookings,
                    status: "partiallyLoaded",
                    comparisonError: error,
                });
            }
        };

        loadAggregatedData({
            groupBy: AGGREGATED_GROUPS_ALLOWED,
            setState: setDataBookingsByCountry,
            withComparison: true,
            dateBy: js.dataType,
        }).catch((error) => {
            if (ignore) return;

            setDataBookingsByCountry({
                status: "error",
                data: [],
                error,
            });
        });

        loadAggregatedData({
            groupBy: AGGREGATED_GROUPS_ALLOWED,
            setState: setDataBookingsByCheckIn,
            withComparison: true,
            dateBy: DATATYPE_CHECKIN,
        }).catch((error) => {
            if (ignore) return;

            setDataBookingsByCheckIn({
                status: "error",
                data: [],
                error,
            });
        });

        loadAggregatedData({
            groupBy: AGGREGATED_GROUPS_ALLOWED,
            setState: setDataBookingsByResDate,
            withComparison: true,
            dateBy: DATATYPE_RESDATE,
        }).catch((error) => {
            if (ignore) return;

            setDataBookingsByResDate({
                status: "error",
                data: [],
                error,
            });
        });

        loadAggregatedData({
            groupBy: AGGREGATED_GROUPS_BY_HOTEL,
            setState: setDataBookingsByHotel,
            withComparison: true,
            dateBy: js.dataType,
        }).catch((error) => {
            if (ignore) return;

            setDataBookingsByHotel({
                status: "error",
                data: [],
                error,
            });
        });

        const loadSearchDatesSummary = async ({ dateBy, setState }) => {
            const cacheKey = ReservationsAggregatedCache.getKey({
                hotelIDs: hotel_ids,
                dateBy,
                source: "search_requests_dates_summary",
            });

            let cache = bookingsCache.current.get(cacheKey);

            if (!cache || cache.usesLeft <= 0) {
                cache = new ReservationsAggregatedCache({
                    headers,
                    hotelIDs: hotel_ids,
                    dateBy,
                    maxUses: CACHE_USES,
                    source: "search_requests_dates_summary",
                });

                bookingsCache.current.set(cacheKey, cache);
            }

            setState({
                status: "loading",
                data: [],
            });

            const currentDataSearchDates = await cache.get(
                js.fromDay,
                js.toDay,
            );

            if (ignore) return;

            setState({
                ...currentDataSearchDates,
                status: "loaded",
                updatedAt: Date.now(),
            });
        };
        loadSearchDatesSummary({
            dateBy: "search_date",
            setState: setDataSearchDatesSummary,
        }).catch((error) => {
            if (ignore) return;

            setDataSearchDatesSummary({
                status: "error",
                data: [],
                error,
            });
        });

        loadSearchDatesSummary({
            dateBy: "stay_date",
            setState: setDataSearchedStayDatesSummary,
        }).catch((error) => {
            if (ignore) return;

            setDataSearchedStayDatesSummary({
                status: "error",
                data: [],
                error,
            });
        });

        return () => {
            ignore = true;
        };
    }, [
        js.fromDay,
        js.toDay,
        js.dataType,
        js.filters?.Comparison,
        js.filters?.ComparisonCustom?.fromDay,
        js.filters?.ComparisonCustom?.toDay,
        hotelsKey,
        authToken,
    ]);

    const reportBuilderKey = useMemo(
        () =>
            [
                hotelsKey,
                js.dataType,
                js.fromDay,
                js.toDay,
                js.filters?.Comparison ?? DEFAULT_COMPARISON,
                js.filters?.ComparisonCustom?.fromDay ?? "",
                js.filters?.ComparisonCustom?.toDay ?? "",
            ].join("|"),
        [
            hotelsKey,
            js.dataType,
            js.fromDay,
            js.toDay,
            js.filters?.Comparison,
            js.filters?.ComparisonCustom?.fromDay,
            js.filters?.ComparisonCustom?.toDay,
        ],
    );

    const dateTypeDescription =
        js.dataType === DATATYPE_RESDATE
            ? "reservation date, meaning the date when the booking was made"
            : "check-in date, meaning the date when the stay starts";

    const SEARCH_DATES_METRICS_INFO = {
        searchesMade: {
            abbreviation: "Searches",
        },
        searchSessions: {
            abbreviation: "Sessions",
        },
        bookedSearchesMade: {
            abbreviation: "Booked",
        },
        stayDateSearches: {
            abbreviation: "Searches",
        },
        stayDateSessions: {
            abbreviation: "Sessions",
        },
        bookedStayDateSearches: {
            abbreviation: "Booked",
        },
    };

    const buildSearchDatesTopData = (dataSearchDates, visibleMetrics) => {
        const out = {};

        (dataSearchDates.data ?? []).forEach((row) => {
            const date = dayjs.utc(row.date);
            const result = {};

            (row.hotels ?? []).forEach((hotel) => {
                [
                    "searchesMade",
                    "searchSessions",
                    "bookedSearchesMade",
                    "stayDateSearches",
                    "stayDateSessions",
                    "bookedStayDateSearches",
                ].forEach((metric) => {
                    result[metric] =
                        (result[metric] ?? 0) + (Number(hotel[metric]) || 0);
                });
            });

            const total = visibleMetrics.reduce(
                (acc, metric) => acc + (Number(result[metric]) || 0),
                0,
            );

            if (!total) return;

            out[row.date] = {
                keys: [
                    [GROUP_DATE_YEAR, date.year()],
                    [GROUP_DATE_MONTH, date.month() + 1],
                    [GROUP_DATE_DAY, date.date()],
                ],
                result,
            };
        });

        return out;
    };

    const searchDatesTopData = useMemo(
        () =>
            buildSearchDatesTopData(dataSearchDatesSummary, [
                "searchesMade",
                "searchSessions",
                "bookedSearchesMade",
            ]),
        [dataSearchDatesSummary],
    );

    const searchedStayDatesTopData = useMemo(
        () =>
            buildSearchDatesTopData(dataSearchedStayDatesSummary, [
                "stayDateSearches",
                "stayDateSessions",
                "bookedStayDateSearches",
            ]),
        [dataSearchedStayDatesSummary],
    );

    return (
        <Container maxWidth={false} sx={{ mt: 2 }}>
            <DataLoader SimpleData />
            <Typography variant="h4">Booked vs Selling</Typography>

            <Typography variant="subtitle1" color="initial" sx={{ mt: 1 }}>
                Compare performance by booked date or stay date with prior
                period benchmarks
            </Typography>

            <FiltersReport
                dataBookingsOverride={dataBookingsByCountry}
                // PDF={true}
                hideExports
                CustomFilters={() => (
                    <HotelSelectorMany
                        selectedHotels={hotels}
                        setSelectedHotels={setHotels}
                        AccountManager
                    />
                )}
                filtersOrder={[
                    "CUSTOM_FILTERS",
                    ...DEFAULT_FILTERS_ORDER.filter(
                        (x) => x !== "CUSTOM_FILTERS",
                    ),
                ]}
                otherProps={{
                    DATATYPE_VALUES: {
                        [DATATYPE_RESDATE]: DATATYPE_RESDATE_NAME,
                        [DATATYPE_CHECKIN]: DATATYPE_CHECKIN_NAME,
                    },
                }}
                hide={[
                    // "TIME_RANGE",
                    // "COMPARISON",
                    // "CANCELLATIONS",
                    "ARRIVAL_PERIOD",
                    "TIME_ARRIVAL_RANGE",
                    "RESDATE_PERIOD",
                    "TIME_BOOKING_RANGE",
                    "TARGET",

                    "FILTERS_OFFERS",
                    "FILTERS_ROOMS",
                    "FILTERS_TYPES",
                    "FILTERS_EXTRAS",
                    "FILTERS_VOUCHERS",
                ]}
                defaultValues={{
                    presetDate: PRESET_MONTH_TO_DATE,
                    presetArrivalDate: PRESET_ANYTIME,
                    presetResDate: PRESET_ANYTIME,
                    dataType: DATATYPE_RESDATE,
                    filters: {
                        Cancellations: "included",
                        Comparison: COMPARISON_SAME_TIME_LAST_YEAR,
                        Target: TARGET_OFF,
                        // ComparisonCustom: {
                        //     ...PresetDates(PRESET_LAST_YEAR),
                        // },
                    },
                }}
            />
            <ReportBuilder
                key={reportBuilderKey}
                dataBookingsOverride={dataBookingsByCountry}
                reports={[
                    {
                        type: REPORT_TABLE_METRICS_SUMMARY,
                        groupBy: [],
                        customProcess: processAggregated_,
                        fieldsOrder: [
                            "revenueTotal",
                            "revenueRoom",
                            "revenueRoomAndExtras",
                            "revenueExtras",
                            "revenueVouchers",
                            "bookingsRooms",
                            "roomNightsTotal",
                            "bookingsCancelled",
                            "cancellationRate",
                            "ABWTotal",
                            "ALoSTotal",
                            "ADRTotal",
                        ],
                        hideFields: Object.keys(DISPLAY_ELEMENT_HELPER).filter(
                            (e) =>
                                ![
                                    "revenueTotal",
                                    "revenueRoom",
                                    "revenueRoomAndExtras",
                                    "revenueVouchers",
                                    "bookingsRooms",
                                    "roomNightsTotal",
                                    "cancellationRate",
                                    "ADRTotal",
                                ].includes(e),
                        ),
                        props: {
                            hideEvents: true,
                        },
                    },
                    {
                        type: REPORT_HOTEL_SUMMARY_TABLE,
                        title: "Hotel summary",
                        dataBookingsOverride: dataBookingsByHotel,
                        customProcess: processAggregated_,
                        groupBy: AGGREGATED_GROUPS_BY_HOTEL,
                        props: {
                            title: "Hotel summary",
                            selectedHotels: hotels,
                            comparisonText: comparisonLabel,
                            description: `Summarises the selected hotels using the selected date type: ${dateTypeDescription}. Each row is one hotel, based on aggregated data only.`,
                            metrics: [
                                "revenueRoom",
                                "bookingsRooms",
                                "roomNightsTotal",
                                "ADRTotal",
                                "ALoSTotal",
                                "ABWTotal",
                                "cancellationRate",
                                "revenueVouchers",
                            ],
                            comparisonMetrics: [
                                "revenueRoom",
                                "bookingsRooms",
                                "roomNightsTotal",
                                "ADRTotal",
                                "ALoSTotal",
                                "ABWTotal",
                                "cancellationRate",
                                "revenueVouchers",
                            ],
                            defaultMetricSort: "revenueRoom",
                        },
                    },
                    {
                        type: REPORT_METRIC_VS_METRIC_BARS,
                        title: "Revenue vs Room nights",
                        customProcess: processAggregated_,
                        groupBy: [
                            GROUP_DATE_YEAR,
                            GROUP_DATE_MONTH,
                            GROUP_DATE_DAY,
                        ],
                        props: {
                            title: "Bookings Rooms vs Room nights",
                            metrics: ["bookingsRooms", "roomNightsTotal"],
                            aggregation: "Week",
                            showLastYear: true,
                            comparisonText: comparisonLabel,
                            description: `Compares bookings and room nights over time, grouped by week. Dates are based on the selected date type: ${dateTypeDescription}.`,
                        },
                    },
                    {
                        type: GRID_HORIZONTAL,
                        spacing: 2,
                        reports: [
                            {
                                type: REPORT_METRIC_PIE,
                                title: "Room Revenue by country",
                                customProcess: processAggregated_,

                                groupBy: [GROUP_COUNTRIES],
                                props: {
                                    title: "Room Revenue by country",
                                    metric: "revenueRoom",
                                    aggregation: "Country",
                                    hole: "45%",
                                    showLegend: true,
                                    comparisonText: comparisonLabel,
                                    description: `Shows how room revenue is split by guest country for the selected period. Dates are based on the selected date type: ${dateTypeDescription}.`,
                                },
                            },
                            {
                                type: REPORT_METRIC_VS_METRIC_BARS,
                                title: "Bookings & Room Nights by Country",
                                customProcess: processAggregated_,

                                groupBy: [GROUP_COUNTRIES],
                                props: {
                                    title: "Bookings Rooms & Room Nights by Country",
                                    aggregation: "Country",
                                    metrics: [
                                        "bookingsRooms",
                                        "roomNightsTotal",
                                    ],
                                    showLastYear: true,
                                    showAllXAxisLabels: false,
                                    othersFrom: 6,
                                    comparisonText: comparisonLabel,
                                    description: `Compares bookings and room nights by guest country for the selected period. Dates are based on the selected date type: ${dateTypeDescription}. Countries from the sixth position onwards are grouped as Others.`,
                                },
                            },
                        ],
                    },
                    {
                        type: REPORT_METRIC_LINE_TREND,
                        title: "Room Revenue trend",
                        customProcess: processAggregated_,
                        groupBy: [
                            GROUP_DATE_YEAR,
                            GROUP_DATE_MONTH,
                            GROUP_DATE_DAY,
                        ],
                        props: {
                            title: "Room Revenue trend",
                            metric: "revenueRoom",
                            aggregation: "Week",
                            showLastYear: true,
                            comparisonText: comparisonLabel,
                            description: `Shows the weekly trend of room revenue for the selected period. Dates are based on the selected date type: ${dateTypeDescription}.`,
                        },
                    },
                    {
                        type: REPORT_METRIC_VS_METRIC_BARS,
                        title: "Room Revenue by day of the week",
                        customProcess: processAggregated_,
                        groupBy: [
                            GROUP_DATE_YEAR,
                            GROUP_DATE_MONTH,
                            GROUP_DATE_DAY,
                        ],
                        props: {
                            title: "Room Revenue by day of the week",
                            metrics: ["revenueRoom"],
                            aggregation: "Weekday",
                            showLastYear: true,
                            comparisonText: comparisonLabel,
                            description: `Shows room revenue grouped by day of the week. Dates are based on the selected date type: ${dateTypeDescription}.`,
                        },
                    },
                    {
                        type: GRID_HORIZONTAL,
                        spacing: 2,
                        fullMd: true,
                        reports: [
                            {
                                type: REPORT_TOP_DATES_TABLE,
                                dataType: DATATYPE_CHECKIN,
                                dataBookingsOverride: dataBookingsByCheckIn,
                                customProcess: processAggregated_,
                                groupBy: [
                                    GROUP_DATE_YEAR,
                                    GROUP_DATE_MONTH,
                                    GROUP_DATE_DAY,
                                ],
                                props: {
                                    title: "Top selling dates",
                                    description:
                                        "Ranks check-in dates by room revenue. Each row is a stay/check-in date, not the date when the booking was made. Sorted by room revenue by default.",
                                    dateType: DATATYPE_CHECKIN,
                                    defaultTop: 10,
                                    metrics: [
                                        "bookingsTotal",
                                        "roomNightsTotal",
                                        "revenueRoom",
                                    ],
                                    defaultMetricSort: "revenueRoom",
                                },
                            },
                            {
                                type: REPORT_TOP_DATES_TABLE,
                                dataType: DATATYPE_RESDATE,
                                dataBookingsOverride: dataBookingsByResDate,
                                customProcess: processAggregated_,
                                groupBy: [
                                    GROUP_DATE_YEAR,
                                    GROUP_DATE_MONTH,
                                    GROUP_DATE_DAY,
                                ],
                                props: {
                                    title: "Top booked dates",
                                    description:
                                        "Ranks reservation dates by total bookings. Each row is the date when the booking was made, not the guest check-in date. Sorted by total bookings by default.",
                                    dateType: DATATYPE_RESDATE,
                                    defaultTop: 10,
                                    metrics: [
                                        "bookingsTotal",
                                        "roomNightsTotal",
                                        "revenueRoom",
                                    ],
                                    defaultMetricSort: "bookingsTotal",
                                },
                            },
                        ],
                    },
                    {
                        type: GRID_HORIZONTAL,
                        spacing: 2,
                        fullMd: true,
                        reports: [
                            {
                                type: REPORT_TOP_DATES_TABLE,
                                title: "Top search dates",
                                cacheKey: `top-search-dates-${dataSearchDatesSummary.updatedAt ?? dataSearchDatesSummary.status ?? ""}`,
                                customProcess: () => ({
                                    entries: searchDatesTopData,
                                    len: Object.keys(searchDatesTopData).length,
                                }),
                                props: {
                                    title: "Top search dates",
                                    description:
                                        "Ranks the dates when users made the most searches. The selected period is applied to the search date. Booked counts searches linked to a booking, not necessarily unique reservations.",
                                    defaultTop: 10,
                                    metrics: [
                                        "searchesMade",
                                        "searchSessions",
                                        "bookedSearchesMade",
                                    ],
                                    defaultMetricSort: "searchesMade",
                                    metricsInfo: SEARCH_DATES_METRICS_INFO,
                                },
                            },
                            {
                                type: REPORT_TOP_DATES_TABLE,
                                title: "Top searched stay dates",
                                cacheKey: `top-searched-stay-dates-${dataSearchedStayDatesSummary.updatedAt ?? dataSearchedStayDatesSummary.status ?? ""}`,

                                customProcess: () => ({
                                    entries: searchedStayDatesTopData,
                                    len: Object.keys(searchedStayDatesTopData)
                                        .length,
                                }),
                                props: {
                                    title: "Top searched stay dates",
                                    description:
                                        "Ranks the stay dates users searched for the most. The selected period is applied to the searched stay date, not to when the search was made. Booked counts searches for that stay date that were linked to a booking, not necessarily unique reservations.",
                                    defaultTop: 10,
                                    metrics: [
                                        "stayDateSearches",
                                        "stayDateSessions",
                                        "bookedStayDateSearches",
                                    ],
                                    defaultMetricSort: "stayDateSearches",
                                    metricsInfo: SEARCH_DATES_METRICS_INFO,
                                },
                            },
                        ],
                    },
                ]}
            />
            <br />
        </Container>
    );
};

export default BookedSellingScreen;
